import { useEffect, useState } from 'react'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { Link2, Loader2, UserCheck } from 'lucide-react'
import { formatDate } from '@/shared/lib/dateUtils'
import { mensajeDeError } from '@/shared/lib/mensajeDeError'
import {
  origenesDeCandidato,
  type ColaboradorDelDirectorio,
} from '@/features/rh/api/origenesDeCandidato'
import type { Candidate } from '@/services/api'

/**
 * Quien recomendo al candidato, y a quien del directorio corresponde (RH-06).
 *
 * Los dos datos se pintan por separado porque son independientes: arriba lo
 * que declaro el candidato, que no se toca nunca, y abajo la decision de RH
 * sobre a quien corresponde. Si se mostraran como uno solo se perderia de
 * vista que el segundo es una interpretacion del primero.
 */
export const RecomendacionDelCandidato = ({
  candidato,
  puedeVincular,
  onVinculado,
}: {
  candidato: Candidate
  puedeVincular: boolean
  onVinculado: () => void
}) => {
  const [colaboradores, setColaboradores] = useState<ColaboradorDelDirectorio[]>([])
  const [elegido, setElegido] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setElegido(
      candidato.referredByEmployeeId
        ? String(candidato.referredByEmployeeId)
        : '',
    )
  }, [candidato.referredByEmployeeId])

  useEffect(() => {
    if (!puedeVincular) return
    let vigente = true
    origenesDeCandidato
      .colaboradores()
      .then((lista) => vigente && setColaboradores(lista))
      .catch(() => undefined)
    return () => {
      vigente = false
    }
  }, [puedeVincular])

  const vincular = async (valor: string) => {
    setElegido(valor)
    setError('')
    setGuardando(true)
    try {
      await origenesDeCandidato.vincularRecomendacion(
        candidato.id,
        valor ? Number(valor) : null,
      )
      onVinculado()
    } catch (errCrudo: unknown) {
      setError(mensajeDeError(errCrudo))
      // Se regresa a lo que habia: dejar elegido lo que no se guardo haria
      // creer que la vinculacion quedo hecha.
      setElegido(
        candidato.referredByEmployeeId
          ? String(candidato.referredByEmployeeId)
          : '',
      )
    } finally {
      setGuardando(false)
    }
  }

  const nombreVinculado = candidato.referredByEmployee?.user
    ? [
        candidato.referredByEmployee.user.firstName,
        candidato.referredByEmployee.user.lastName,
      ]
        .filter(Boolean)
        .join(' ')
    : null

  const quienVinculo = candidato.referralLinkedBy
    ? [candidato.referralLinkedBy.firstName, candidato.referralLinkedBy.lastName]
        .filter(Boolean)
        .join(' ')
    : null

  return (
    <div className="bg-white rounded-xl shadow-sm border border-[#E0E0E0] p-6 mb-6">
      <h3 className="text-base font-semibold text-[#1E1E1F] mb-4 flex items-center gap-2">
        <UserCheck className="w-4 h-4 text-[#3D4B8F]" />
        Recomendación
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <p className="text-sm text-gray-500 mb-1">Declaró que lo recomendó:</p>
          <p className="text-sm font-medium text-[#1E1E1F]">
            {candidato.referredByText || 'No lo especificó'}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500 mb-1">Colaborador vinculado:</p>
          {nombreVinculado ? (
            <>
              <p className="text-sm font-medium text-[#1E1E1F]">
                {nombreVinculado}
              </p>
              {quienVinculo && candidato.referralLinkedAt && (
                <p className="text-xs text-[#787878] mt-1">
                  Vinculado por {quienVinculo} el{' '}
                  {formatDate(candidato.referralLinkedAt)}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-[#787878] italic">Sin vincular</p>
          )}
        </div>
      </div>

      {puedeVincular && (
        <div className="mt-5 pt-5 border-t border-[#E0E0E0]">
          <label
            htmlFor="vincular-colaborador"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            <Link2 className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Vincular con el directorio
          </label>
          <div className="flex items-center gap-3">
            <SelectBuscable
              variante="formulario"
              id="vincular-colaborador"
              value={elegido}
              disabled={guardando}
              onChange={vincular}
              className="w-full max-w-md"
              placeholder="Sin vincular"
              placeholderDeBusqueda="Buscar colaborador..."
              opciones={colaboradores.map((c) => ({
                value: String(c.id),
                label: [c.fullName, c.puestoName, c.areaName]
                  .filter(Boolean)
                  .join(' · '),
              }))}
            />
            {guardando && (
              <Loader2 className="w-4 h-4 animate-spin text-[#477FFA]" />
            )}
          </div>
          <p className="text-xs text-[#787878] mt-2">
            Vincular no cambia lo que declaró el candidato.
          </p>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </div>
      )}
    </div>
  )
}
